import { Controller } from "stimulus"

// Intercepts the attempt form's plain save when the status was changed and
// other attempts on the same parcel are scheduled that day, and offers to
// apply the new status to them too. Checkboxes are rendered disabled so
// nothing extra is submitted unless the modal is shown. Each checkbox carries
// its inspection type's status list (data-states); rows whose type lacks the
// chosen status stay disabled and are marked unavailable. Declining unchecks
// everything before submitting.
export default class extends Controller {
    static targets = ["modal", "checkbox", "status", "statusLabel", "confirmButton"]

    intercept(event) {
        if (this.confirmed) return
        if (event.defaultPrevented) return
        if (!this.hasModalTarget || !this.hasStatusTarget) return
        if (this.submitsElsewhere(event)) return

        const status = this.statusTarget.value
        if (!status || status === this.data.get("originalStatus")) return
        if (!this.checkboxTargets.some((checkbox) => this.offers(checkbox, status))) return

        event.preventDefault()
        this.submitter = event.submitter
        this.prepareCheckboxes(status)
        if (this.hasStatusLabelTarget) this.statusLabelTarget.textContent = status
        this.modalTarget.classList.remove("hidden")
        if (this.hasConfirmButtonTarget) this.confirmButtonTarget.focus()
    }

    confirm(event) {
        event.preventDefault()
        this.submitForm()
    }

    decline(event) {
        event.preventDefault()
        this.checkboxTargets.forEach((checkbox) => { checkbox.checked = false })
        this.submitForm()
    }

    close(event) {
        event.preventDefault()
        this.checkboxTargets.forEach((checkbox) => { checkbox.disabled = true })
        this.modalTarget.classList.add("hidden")
        this.restoreFocus()
    }

    // Returns focus to the button that opened the dialog (or the status
    // select) so keyboard users are not left on a hidden element.
    restoreFocus() {
        const opener = this.submitter && this.submitter.isConnected ? this.submitter : this.statusTarget
        if (opener && typeof opener.focus === "function") opener.focus()
    }

    // Bound on the modal wrapper: Escape closes from any control inside it,
    // and Tab / Shift+Tab wrap at the ends so focus stays in the dialog
    // (it declares aria-modal).
    keydown(event) {
        if (event.key === "Escape") {
            this.close(event)
        } else if (event.key === "Tab") {
            this.trapFocus(event)
        }
    }

    trapFocus(event) {
        const focusable = this.focusableElements()
        if (focusable.length === 0) return

        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (event.shiftKey && event.target === first) {
            event.preventDefault()
            last.focus()
        } else if (!event.shiftKey && event.target === last) {
            event.preventDefault()
            first.focus()
        }
    }

    // Enabled controls in the dialog, in document order; unavailable
    // (disabled) rows are skipped.
    focusableElements() {
        return Array.from(this.modalTarget.querySelectorAll("button, input, select, textarea, a[href]"))
            .filter((element) => !element.disabled)
    }

    // Every row whose inspection type offers the status starts checked;
    // the rest are disabled, unchecked, and flagged for the "not available"
    // note.
    prepareCheckboxes(status) {
        this.checkboxTargets.forEach((checkbox) => {
            const available = this.offers(checkbox, status)
            checkbox.disabled = !available
            checkbox.checked = available
            const row = checkbox.closest("label")
            if (row) row.dataset.unavailable = String(!available)
        })
    }

    offers(checkbox, status) {
        try {
            return JSON.parse(checkbox.dataset.states || "[]").includes(status)
        } catch (_error) {
            return false
        }
    }

    // Re-submits with the originally intercepted submitter so the browser
    // keeps that button's name/value in the submission.
    submitForm() {
        this.confirmed = true
        this.modalTarget.classList.add("hidden")
        const submitter = this.submitter && this.submitter.isConnected ? this.submitter : null
        if (typeof this.element.requestSubmit === "function") {
            this.element.requestSubmit(submitter)
        } else if (submitter) {
            submitter.click()
        } else {
            this.element.submit()
        }
    }

    // Buttons that post elsewhere via formaction (reschedule / cancel) are
    // outside this prompt; it only applies to the plain save.
    submitsElsewhere(event) {
        return Boolean(event.submitter && event.submitter.getAttribute("formaction"))
    }
}
